import React, { useState, useEffect } from 'react';
import CheckboxValue from './value-components/CheckboxValue';
import FileValue from './value-components/FileValue';
import RadioValue from './value-components/RadioValue';
import SelectValue from './value-components/SelectValue';
import TextAreaValue from './value-components/TextAreaValue';
import TextValue from './value-components/TextValue';
import {
  createResponseOption,
  destroyResponseOption,
  updateResponse,
  updateResponseOption,
  createResponseFile,
  updateResponseFile,
  destroyResponseFile,
  copyResponseFile,
} from '../constants/handleResponseData';
import { decodeHTML } from 'entities';

const FieldResponseDisplay = (props) => {
  const [mutableResponse, setMutableResponse] = useState(props.response);
  const {
    setResponses,
    setQuotaOpt,
    setShowQuotaStatusModal,
    setPrevQuotaOpt,
    field,
  } = props;
  // additional props passed in: { field, response, isEditing, adminField }
  // choosing not to destructure all props here to keep the return switch statement cleaner.
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    // Update hoisted state when a response is changed
    if (mutableResponse) {
      setResponses((rs) => {
        return [
          ...rs.filter((r) => r.id != mutableResponse.id),
          mutableResponse,
        ];
      });
    }
  }, [mutableResponse]);

  const handleTextResponseUpdate = async (e, response) => {
    const currentDecodedValue = response.value
      ? decodeHTML(response.value)
      : '';
    if (e.target.value != currentDecodedValue) {
      let updated = await updateResponse(response.id, e.target.value);
      if (updated.status === 200) {
        setMutableResponse(updated.data);
        return updated.data.value;
      }
    }
    return e.target.value;
  };

  const handleDestroyFile = async (rfId) => {
    await destroyResponseFile(rfId, (result) => {
      if (result.ok) {
        setMutableResponse({
          ...mutableResponse,
          response_files: [
            ...mutableResponse.response_files.filter((rf) => rf.id != rfId),
          ],
        });
      }
    });
  };

  const handleAddFile = async (htmlForm) => {
    setIsUploading(true);
    let result = await createResponseFile(mutableResponse.id, htmlForm);
    if (result.ok) {
      setMutableResponse({
        ...mutableResponse,
        response_files: [...mutableResponse.response_files, result.data],
      });
    }
    setIsUploading(false);
  };

  const handleFileCopy = async (responseID, responseFileID) => {
    let result = await copyResponseFile(responseID, responseFileID);
    if (result.ok) {
      setMutableResponse({
        ...mutableResponse,
        response_files: [...mutableResponse.response_files, result.data],
      });
    }
    return result;
  };

  const handleUpdateFilename = async (e, rfId, newName) => {
    let result = await updateResponseFile(rfId, newName);
    if (result.ok) {
      setMutableResponse((res) => {
        let index = res.response_files.map((rf) => rf.id).indexOf(rfId);
        res.response_files[index] = result.data;
        return { ...res };
      });
    }
  };

  const handleResponseOptionUpdate = async (e, response, optId) => {
    let ro;
    if (e.target.type != 'checkbox') {
      ro = response.response_options.length
        ? response.response_options[0]
        : null;
    }
    switch (e.target.type) {
      case 'checkbox':
        if (e.target.checked) {
          ro = await createResponseOption(response.id, optId);
          setMutableResponse((res) => {
            res.response_options = [
              ...res.response_options,
              ro.data.response_option,
            ];
            return { ...res };
          });
        } else {
          ro = response.response_options.find((ro) => ro.option_id === optId);
          let confirm = await destroyResponseOption(ro.id);
          if (confirm.ok)
            setMutableResponse((res) => {
              res.response_options = res.response_options.filter(
                (rro) => rro.id != ro.id
              );
              return { ...res };
            });
        }
        break;

      case 'radio':
        if (ro) {
          ro = await updateResponseOption(ro.id, optId);
        } else {
          ro = await createResponseOption(response.id, optId);
        }
        setMutableResponse((res) => {
          res.response_options = [ro.data.response_option];
          return { ...res };
        });
        break;

      case 'select-one':
        if (ro && !optId) {
          let confirm = await destroyResponseOption(ro.id);
          if (confirm.ok)
            setMutableResponse((res) => {
              res.response_options = [];
              return { ...res };
            });
          if (field.informs_global) {
            setPrevQuotaOpt(confirm.data?.prev_opt);
            setShowQuotaStatusModal(confirm.data?.prev_opt ? true : false);
          }
        } else {
          ro
            ? (ro = await updateResponseOption(ro.id, optId))
            : (ro = await createResponseOption(response.id, optId));
          setMutableResponse((res) => {
            res.response_options = [ro.data.response_option];
            return { ...res };
          });
          if (field.informs_global) {
            setPrevQuotaOpt(ro.data?.prev_opt);
            setQuotaOpt(ro.data?.response_option?.option);
            setShowQuotaStatusModal(true);
          }
        }
        break;

      default:
        return console.log('Unknown event input type');
    }
  };

  switch (field.type) {
    case 'checkbox':
      return (
        <CheckboxValue
          {...{
            ...props,
            response: mutableResponse,
            handleResponseOptionUpdate,
          }}
        />
      );
    case 'file':
      return (
        <FileValue
          {...{
            ...props,
            response: mutableResponse,
            handleAddFile,
            handleDestroyFile,
            handleUpdateFilename,
            handleFileCopy,
            isUploading,
          }}
        />
      );
    case 'radio':
      return (
        <RadioValue
          {...{
            ...props,
            response: mutableResponse,
            handleResponseOptionUpdate,
          }}
        />
      );
    case 'select':
      return (
        <SelectValue
          {...{
            ...props,
            response: mutableResponse,
            handleResponseOptionUpdate,
          }}
        />
      );
    case 'textarea':
      return (
        <TextAreaValue
          {...{ ...props, response: mutableResponse, handleTextResponseUpdate }}
        />
      );
    case 'text':
      return (
        <TextValue
          {...{ ...props, response: mutableResponse, handleTextResponseUpdate }}
        />
      );
    default:
      return <p></p>;
  }
};

export default FieldResponseDisplay;
